<script setup lang="ts">
// Types
import type { RouteLocationRaw } from "vue-router";

// Constants
import { GENERIC_STATE } from "../../shared/types/constants";

// Composables
const localePath = useLocalePath();
const attrs = useAttrs();
const { t } = useI18n();

const {
  external = false,
  variant = "primary",
  size = "small",
  to,
  reverse = false,
  state = GENERIC_STATE.IDLE,
  icon,
  ariaLabelLoading,
} = defineProps<{
  external?: boolean;
  variant?: "primary" | "secondary" | "ghost";
  size?: "small" | "large";
  to?: RouteLocationRaw;
  state?: GenericState;
  ariaLabelLoading?: string;
  /**
   * Name of the icon to use, if any (e.g. 'foundation:chevron-left')
   */
  icon?: string;
  /**
   * If true, icon will be placed after the label
   */
  reverse?: boolean;
}>();

const component = computed(() =>
  to ? resolveComponent("NuxtLink") : "button",
);

const resolvedRoute = computed(() => {
  if (component.value === "button" || !to) {
    return undefined;
  }

  return localePath(to);
});

// Button is disabled if disabled attribute is passed OR if state is processing
const isDisabled = computed(
  () => !!attrs.disabled || state === GENERIC_STATE.PROCESSING,
);

const isLoading = computed(() => state === GENERIC_STATE.PROCESSING);

const activeIcon = computed(() => {
  if (isLoading.value) {
    return "foundation:spinner";
  }

  return icon;
});

const ariaLabel = computed(() => {
  if (isLoading.value) {
    return ariaLabelLoading ? t(ariaLabelLoading) : t("forms.state.loading");
  }
  return (attrs["aria-label"] as string) || undefined;
});
</script>

<template>
  <component
    :is="component"
    :external="to ? external : undefined"
    :to="resolvedRoute"
    :disabled="isDisabled"
    :class="[
      'foundation-base-button',
      `foundation-base-button--${variant}`,
      `foundation-base-button--${size}`,
      { 'foundation-base-button--loading': isLoading },
    ]"
    data-testid="foundation-base-button"
    :aria-label
    :aria-disabled="isDisabled || undefined"
    :aria-busy="isLoading"
  >
    <span
      :class="[
        'foundation-base-button__label',
        `foundation-base-button__label--${variant}`,
        `foundation-base-button__label--${size}`,
        { 'foundation-base-button__label--icon': icon },
        { 'foundation-base-button__label--reverse': reverse },
      ]"
      data-testid="foundation-base-button-label"
    >
      <Icon
        v-if="activeIcon"
        :name="activeIcon"
        aria-hidden="true"
        class="foundation-base-button__icon"
        data-testid="foundation-base-button-icon"
      />

      <slot />
    </span>
  </component>
</template>

<style lang="scss">
@use "../../assets/styles/mixins" as *;
@include spin-animation;

// Small primary is default combination
.foundation-base-button {
  $self: &;

  display: inline-block;
  color: var(--cm-color-white, #fff);
  font-weight: var(--cm-font-weight-bold, 700);
  white-space: nowrap;
  text-decoration: none;
  background-color: var(--cm-color-primary, #007bff);
  border: 1px solid var(--cm-color-primary, #007bff);
  border-radius: var(--cm-border-radius, 0.25rem);
  transition: var(--cm-transition-fast, 200ms);
  padding-block: var(--cm-spacing-2, 0.5rem);
  padding-inline: var(--cm-spacing-6, 1.5rem);

  &--large {
    padding-block: var(--cm-spacing-4, 1rem);
    padding-inline: var(--cm-spacing-8, 2rem);
  }

  &--loading {
    cursor: wait;
  }

  &--loading &__icon {
    animation: spin 1s linear infinite;
  }

  &__label {
    &:not(&--icon) {
      display: block;
    }

    overflow: hidden;
    font-size: 1rem;
    line-height: 1.5;
    text-align: center;
    text-overflow: ellipsis;

    &--reverse {
      flex-direction: row-reverse;
    }

    &--large {
      font-size: 1.125rem;
    }
  }

  &__label--icon,
  &--loading &__label {
    display: flex;
    gap: var(--cm-spacing-1, 0.25rem);
    align-items: center;
  }

  &--loading &__label {
    &:not(&--icon) {
      justify-content: center;
    }
  }

  &:disabled {
    color: var(--cm-color-neutral-500, #6c757d);
    background-color: var(--cm-color-neutral-50, #e0e0e0);
    border-color: var(--cm-color-neutral-50, #e0e0e0);

    &:not(&--loading) {
      cursor: not-allowed;
    }
  }

  &--primary {
    @media (hover: hover) {
      &:hover {
        &:not(:disabled) {
          background-color: var(--cm-color-primary-darker, #0056b3);
          border-color: var(--cm-color-primary-darker, #0056b3);
        }
      }
    }

    &:active {
      &:not(:disabled) {
        background-color: var(--cm-color-primary-darkest, #004085);
        border-color: var(--cm-color-primary-darkest, #004085);
      }
    }
  }

  &--secondary {
    color: var(--cm-color-black, #007bff);
    background-color: var(--cm-color-white, #fff);
    border: 1px solid var(--cm-color-black, #000);

    @media (hover: hover) {
      &:hover {
        &:not(:disabled) {
          color: var(--cm-color-white, #fff);
          background-color: var(--cm-color-black, #000);
        }
      }
    }

    &:active {
      &:not(:disabled) {
        color: var(--cm-color-white, #fff);
        background-color: var(--cm-color-black, #000);
      }
    }
  }

  &--ghost {
    color: var(--cm-color-black, #000);
    background-color: transparent;
    border: none;
    padding-inline: 0;

    @media (hover: hover) {
      &:hover {
        &:not(:disabled) {
          color: var(--cm-color-primary, #007bff);
        }
      }
    }

    &:active {
      &:not(:disabled) {
        color: var(--cm-color-primary-darkest, #004085);
      }
    }
  }
}
</style>
